import { useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { trpc } from "@/trpc";
import {
  MemberStatus,
  MemberCategory,
  MEMBER_STATUSES,
  MEMBER_CATEGORIES,
} from "@satyrsmc/shared/lib/enums";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Mail } from "lucide-react";
import { Button } from "@satyrsmc/shared/components";
import { formatMemberSince } from "@/components/members/memberUtils";
import { EmailRosterDialog } from "@/components/members/EmailRosterDialog";
import { useUrlSearchSync } from "@/queries/hooks";

const STATUS_LABELS: Record<MemberStatus, string> = {
  [MemberStatus.Active]: "Active",
  [MemberStatus.Delinquent]: "Delinquent",
  [MemberStatus.Inactive]: "Inactive",
  [MemberStatus.Loa]: "Leave of Absence",
  [MemberStatus.Removed]: "Removed",
};

const CATEGORY_LABELS: Record<MemberCategory, string> = {
  [MemberCategory.Local]: "Local",
  [MemberCategory.Remote]: "Remote",
  [MemberCategory.OutOfState]: "Out of State",
  [MemberCategory.Emeritus]: "Emeritus",
};

type TenureFilter = "all" | "officers" | "loa";

export function MembersContactPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [emailRosterOpen, setEmailRosterOpen] = useState(false);

  const rawStatus = searchParams.get("status") ?? "all";
  const rawCategory = searchParams.get("category") ?? "all";
  const rawTenure = (searchParams.get("tenure") ?? "all") as TenureFilter;

  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams);
  const rawQ = searchParams.get("q") ?? "";

  const { data: members, isLoading } = trpc.admin.members.list.useQuery();

  const filtered = (members ?? []).filter((m) => {
    const matchesStatus = rawStatus === "all" || m.status === rawStatus;
    const matchesCategory = rawCategory === "all" || m.category === rawCategory;
    const matchesTenure =
      rawTenure === "all" ||
      (rawTenure === "officers" &&
        m.position != null &&
        m.position !== "Member" &&
        m.status !== MemberStatus.Removed) ||
      (rawTenure === "loa" && m.status === MemberStatus.Loa);
    const matchesSearch = !rawQ || m.name.toLowerCase().includes(rawQ.toLowerCase());
    return matchesStatus && matchesCategory && matchesTenure && matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Members</h1>
          <p className="mt-1 text-muted-foreground">
            {isLoading
              ? "Loading…"
              : `${filtered.length} member${filtered.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <Button variant="outline" className="gap-1.5" onClick={() => setEmailRosterOpen(true)}>
          <Mail className="size-4" />
          Email Roster
        </Button>
      </div>

      <EmailRosterDialog open={emailRosterOpen} onOpenChange={setEmailRosterOpen} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search members…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
        <Select
          value={rawStatus}
          onValueChange={(v) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v === "all") next.delete("status");
                else next.set("status", v);
                return next;
              },
              { replace: true },
            )
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {MEMBER_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={rawCategory}
          onValueChange={(v) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v === "all") next.delete("category");
                else next.set("category", v);
                return next;
              },
              { replace: true },
            )
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {MEMBER_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={rawTenure}
          onValueChange={(v) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v === "all") next.delete("tenure");
                else next.set("tenure", v);
                return next;
              },
              { replace: true },
            )
          }
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Tenure" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All members</SelectItem>
            <SelectItem value="officers">Officers</SelectItem>
            <SelectItem value="loa">On LOA</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-4 py-3">Name</TableHead>
              <TableHead className="px-4 py-3">Status</TableHead>
              <TableHead className="px-4 py-3">Category</TableHead>
              <TableHead className="px-4 py-3">Position</TableHead>
              <TableHead className="px-4 py-3">Member Since</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              (["s0", "s1", "s2", "s3", "s4"] as const).map((k) => (
                <TableRow key={k}>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No members found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((m) => {
                const hasContact = !!m.contact_id;
                return (
                  <TableRow
                    key={m.id}
                    className={
                      hasContact ? "cursor-pointer" : "cursor-default opacity-50 hover:bg-inherit"
                    }
                    onClick={
                      hasContact
                        ? () =>
                            navigate(`/admin/contacts/${m.contact_id}/member`, {
                              state: { from: location.pathname },
                            })
                        : undefined
                    }
                  >
                    <TableCell className="px-4 py-3 font-medium">{m.name}</TableCell>
                    <TableCell className="px-4 py-3 text-muted-foreground">
                      {m.status ? STATUS_LABELS[m.status] : "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-muted-foreground">
                      {m.category ? CATEGORY_LABELS[m.category] : "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-muted-foreground">
                      {m.position ?? "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-muted-foreground">
                      {formatMemberSince(m.member_since ?? null)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
